import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { cleanReportContent, renderMarkdown } from "@/lib/markdown";

/**
 * Copies a report to the clipboard as rich text, with a brief check-mark
 * confirmation. Sits next to the per-report download button.
 *
 * Writes both `text/html` (the same rendered markup shown in the report view)
 * and `text/plain` (the cleaned markdown). Pasting into Google Docs / Word /
 * Notion keeps the formatting; pasting into a code editor yields markdown.
 * Falls back to plain-text writeText() on browsers without ClipboardItem.
 */
export function CopyButton({ content, className }: { content: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = async () => {
    const markdown = cleanReportContent(content);
    const html = renderMarkdown(content);

    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([markdown], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(markdown);
      }
      flashCopied();
    } catch (err) {
      // Some browsers reject clipboard.write() for text/html — fall back to plain text.
      try {
        await navigator.clipboard.writeText(markdown);
        flashCopied();
      } catch (fallbackErr) {
        console.error("Copy to clipboard failed:", fallbackErr ?? err);
      }
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={
        className ??
        "inline-flex items-center text-sm px-2 py-1 rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
      }
      title={copied ? "Copied!" : "Copy to clipboard"}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}
